import type { ApolloError } from '@apollo/client';
import * as React from 'react';
import classNames from 'classnames';
import { QueryResult } from '@apollo/client';
import get from 'lodash/get';
import {
  Heading,
  DataTable,
  TableFootnote,
  FetchDataArgument,
  GroupedRowsDataTable,
  Text,
  Button,
  TableDatum,
  getIcon,
  Icon,
} from '@quality24/design-system';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';

import {
  Customizations,
  DateFilter,
  HasPermissionFn,
  TableComponentData,
} from '../../../../@types';

import { parseColumns } from './utils';
import { mapData } from '../../utils/mapper';
import { filterData } from '../../utils/filter';
import { createReferenceMap } from '../../utils/reference';

import styles from '../../DataViewContainer.module.scss';
import { parseDataValue, sanitizeKey } from '../../../../utils';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  error?: ApolloError;
  refetch: () => void;
  data: Record<string, unknown>;
  dateFilter: DateFilter;
  config: Omit<TableComponentData, 'type'>;
  // Table pagination
  pageSize: number;
  fetchMore: QueryResult['fetchMore'];
  onChangePageSize: React.Dispatch<React.SetStateAction<number>>;
  onChangePage: React.Dispatch<React.SetStateAction<number>>;
  customizations?: Customizations;
  hasPermission?: HasPermissionFn;
}

/**
 * <TableContainer> page
 */
export const TableContainer: React.FunctionComponent<Props> = ({
  className,
  style,
  loading,
  error,
  refetch,
  data,
  dateFilter,
  config: {
    id,
    title,
    className: configClassName,
    initialSortBy,
    options,
    paginationKey,
    summaryBadges,
    postQuery,
    aggrKey,
    rootKey,
    references,
    groupBy,
    subRows,
  },
  pageSize,
  fetchMore,
  onChangePageSize,
  onChangePage,
  customizations,
  hasPermission,
  ...htmlElementProps
}) => {
  /**
   * Gets the aggregation data for this container
   */
  const aggrData = React.useMemo(
    () => filterData(data, rootKey, postQuery),
    [data, rootKey, postQuery],
  );

  /**
   * Creates the reference map
   */
  const referenceMap = React.useMemo(
    () => createReferenceMap({ data, references, dateFilter, postQuery }),
    [data, references, dateFilter, postQuery],
  );

  /**
   * Create columns
   */
  const columns = React.useMemo(
    () =>
      parseColumns(
        groupBy.filter(
          (group) =>
            !hasPermission ||
            !group.permission ||
            (group?.permission && hasPermission(group?.permission)),
        ),
        referenceMap,
        options,
        customizations?.customCellComponents,
      ),
    [
      customizations?.customCellComponents,
      groupBy,
      hasPermission,
      options,
      referenceMap,
    ],
  );

  /**
   * Maps data
   */
  const tableData = React.useMemo(
    () =>
      mapData({
        data: aggrData,
        groupBy,
        aggrKey,
        reference: referenceMap,
        subRows,
      }),
    [aggrData, groupBy, aggrKey, referenceMap, subRows],
  );

  /** Map data for summary badge */
  const summaryDatas = React.useMemo(
    () =>
      summaryBadges?.length
        ? summaryBadges.map(
            (summaryBadge) =>
              mapData({
                // A badge can aggregate a different node than the table via its
                // own `rootKey`; otherwise it uses the table's aggregated data.
                data: summaryBadge.rootKey
                  ? filterData(data, summaryBadge.rootKey)
                  : aggrData,
                groupBy: summaryBadge.groupBy,
                aggrKey: summaryBadge.aggrKey,
                reference: referenceMap,
              }) as TableDatum[],
          )
        : undefined,
    [aggrData, summaryBadges, referenceMap, data],
  );

  const totalEntries = React.useMemo(
    () =>
      paginationKey
        ? (get(data, paginationKey, 0) as number)
        : tableData.length,
    [data, tableData, paginationKey],
  );

  // Pagination
  const pageCount = React.useMemo(
    () => Math.ceil(totalEntries / pageSize),
    [totalEntries, pageSize],
  );

  // Expanded info
  const startExpanded = React.useMemo(
    () =>
      options?.expanded
        ? tableData.reduce((acc, _c, idx) => {
            acc[`${idx}`] = true;
            return acc;
          }, {} as Record<string, boolean>)
        : {},
    [tableData, options?.expanded],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('TableContainer', {
      originalData: data,
      aggregatedData: aggrData,
      referenceMap,
      mappedData: tableData,
      columns,
      pagination: { totalEntries, pageCount },
    });
  }

  /**
   * Handle table pagination using `fetchMore` function
   */
  const handlePageChange = React.useCallback(
    ({ pageIndex, pageSize: controlledPageSize }: FetchDataArgument) => {
      fetchMore({
        variables: {
          limit: controlledPageSize,
          offset: pageIndex * controlledPageSize,
        },
      }).then(() => {
        onChangePageSize(controlledPageSize);
        onChangePage(pageIndex * controlledPageSize);
      });
    },
    [fetchMore, onChangePageSize, onChangePage],
  );

  return (
    <div
      className={classNames(className, configClassName)}
      style={{ width: '100%', ...style }}
      {...htmlElementProps}
    >
      {title && (
        <Heading as="h2" size="sm2" weight="bold" className="mb-4">
          {!loading && parseDataValue(data, title, 'string')}
        </Heading>
      )}

      {summaryBadges?.length && summaryDatas?.length && (
        <div className="d-flex gap-2">
          {summaryBadges.map((summaryBadge, summaryBadgeIndex) => {
            const summaryData = summaryDatas[summaryBadgeIndex];
            return (
              <div className="d-flex gap-2 align-items-center m-2">
                {!!summaryBadge.icon && (
                  <div
                    className="rounded-md bg-blue600_25 d-flex align-items-center justify-content-center"
                    style={{ height: 46, width: 46 }}
                  >
                    <Icon icon={getIcon(summaryBadge.icon)} color="primary" />
                  </div>
                )}
                <div className="mb-2">
                  <Text size="sm2">{`${summaryBadge.label}:`}</Text>
                  <Text weight="bold" size="lg2">
                    {parseDataValue(
                      summaryData[0],
                      summaryBadge.value,
                      'string',
                    ) || '-'}
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && error && (
        <div className={styles['error-container']}>
          <Text className="mb-2" size="sm2" color="gray100">
            Não foi possível carregar os dados.
          </Text>
          <Button variant="outline" icon={faRefresh} onClick={() => refetch()}>
            Recarregar
          </Button>
        </div>
      )}

      {!error && subRows && (
        <GroupedRowsDataTable
          tableId={title || 'tableId'}
          data={tableData}
          columns={columns}
          expanded={startExpanded}
          // Table pagination
          pagination={options?.pagination || false}
          pageCount={pageCount}
          fetchData={handlePageChange}
          loading={loading}
          initialSortBy={initialSortBy?.map((rule) => ({
            id: sanitizeKey(rule.key),
            desc: rule.direction === 'desc',
          }))}
        />
      )}
      {!error && !subRows && (
        <DataTable
          tableId={id || title || 'tableId'}
          data={tableData}
          columns={columns}
          // Table pagination
          autoPagination={options?.autoPagination || false}
          pagination={options?.pagination || false}
          pageCount={pageCount}
          totalCount={totalEntries}
          fetchData={handlePageChange}
          loading={loading}
          initialSortBy={initialSortBy?.map((rule) => ({
            id: sanitizeKey(rule.key),
            desc: rule.direction === 'desc',
          }))}
        />
      )}

      {options?.footnote && <TableFootnote className="mt-2" />}
    </div>
  );
};

export default TableContainer;
